import type { EntryAudienceExperimentSplitGroup } from "src/types/journeys";

/**
 * The slice of an AudiencesOfParentModel option the experiment entry picker
 * needs to determine eligibility and capture split groups.
 */
export type AudienceExperimentPickerOption = {
  is_realtime_audience: boolean;
  splits: {
    id: string;
    friendly_name: string;
    column_value: string;
    is_holdout_group: boolean;
  }[];
  // `visual_query_filter(path: "$.splitTestDefinition.samplingType")` — a
  // jsonb path selection, so the generated type is `any`.
  split_sampling_type?: unknown;
  connection?: {
    audience_snapshotting_enabled?: boolean | null;
  } | null;
};

export type AudienceExperimentIneligibleReason =
  | "realtime_audience"
  | "no_split_groups"
  | "snapshotting_disabled"
  | "stratified_splits_unstable";

/**
 * Why an audience can't source an audience-experiment entry, or undefined if
 * it's eligible. Mirrors the backend enable-time gate
 * (validateEntryAudienceExperimentReadiness): batch audiences only, split
 * groups configured, snapshotting enabled (the snapshot is the source of
 * entrants), and stratified splits only when stable stratification is enabled
 * for the workspace.
 */
export const getAudienceExperimentIneligibleReason = (
  audience: AudienceExperimentPickerOption,
  args: { stableStratificationEnabled: boolean },
): AudienceExperimentIneligibleReason | undefined => {
  if (audience.is_realtime_audience) {
    return "realtime_audience";
  }
  if (audience.splits.length === 0) {
    return "no_split_groups";
  }
  if (!audience.connection?.audience_snapshotting_enabled) {
    return "snapshotting_disabled";
  }
  if (
    audience.split_sampling_type === "stratified" &&
    !args.stableStratificationEnabled
  ) {
    return "stratified_splits_unstable";
  }
  return undefined;
};

const INELIGIBLE_REASON_MESSAGES: Record<
  AudienceExperimentIneligibleReason,
  string
> = {
  realtime_audience:
    "Realtime audiences can't be used for audience experiments",
  no_split_groups: "This audience doesn't have split groups configured",
  snapshotting_disabled:
    "Enable audience snapshots on this source to use this audience",
  stratified_splits_unstable:
    "This audience uses stratified splits, which require stable split assignments. Stable assignments are not enabled for this workspace.",
};

export const getAudienceExperimentIneligibleMessage = (
  reason: AudienceExperimentIneligibleReason | undefined,
): string | undefined =>
  reason ? INELIGIBLE_REASON_MESSAGES[reason] : undefined;

/**
 * Captures an audience's split groups into the entry node config shape.
 * Values are trimmed here (config-capture time) because entry routing matches
 * ht_split_group values with exact string equality.
 */
export const toEntryAudienceExperimentSplitGroups = (
  splits: AudienceExperimentPickerOption["splits"],
): EntryAudienceExperimentSplitGroup[] =>
  splits.map((split) => ({
    id: split.id,
    name: split.friendly_name,
    value: split.column_value.trim(),
    isHoldout: split.is_holdout_group,
  }));

/** The slice of an audience split needed to detect config drift. */
export type AudienceExperimentSplitForDrift = {
  id: string;
  column_value: string;
};

/**
 * Compares the entry node's captured split groups against the audience's
 * current splits and describes any divergence, or undefined when they still
 * match. Mirrors the backend enable-time drift gate
 * (assertEntryAudienceExperimentReadyIfEnabled): renames are matched by
 * audience_splits.id, adds/removes by the trimmed group value. Surfaced as a
 * validation error so the existing entry-tile error glow lights up.
 */
export const getAudienceExperimentSplitGroupDrift = (
  splitGroups: EntryAudienceExperimentSplitGroup[],
  currentSplits: AudienceExperimentSplitForDrift[],
): string | undefined => {
  const currentSplitsById = new Map(
    currentSplits.map((split) => [split.id, split]),
  );
  const currentValues = new Set(
    currentSplits.map((split) => split.column_value.trim()),
  );
  const configuredValues = new Set(splitGroups.map((group) => group.value));

  const problems: string[] = [];
  const renamedToValues = new Set<string>();
  for (const group of splitGroups) {
    const currentSplit = currentSplitsById.get(group.id);
    if (currentSplit) {
      const currentValue = currentSplit.column_value.trim();
      if (currentValue !== group.value) {
        problems.push(
          `group "${group.value}" was renamed to "${currentValue}"`,
        );
        renamedToValues.add(currentValue);
      }
    } else if (!currentValues.has(group.value)) {
      problems.push(`group "${group.value}" was removed`);
    }
  }
  for (const split of currentSplits) {
    const value = split.column_value.trim();
    if (!configuredValues.has(value) && !renamedToValues.has(value)) {
      problems.push(`group "${value}" was added`);
    }
  }

  return problems.length > 0 ? problems.join("; ") : undefined;
};
